import React, { useMemo, useState } from 'react';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import './SeeristAutomatedSectionContent.css';

function parseSeeristPayload(dataString) {
  if (!dataString) return null;
  try {
    const parsed =
      typeof dataString === 'string' ? JSON.parse(dataString) : dataString;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!Array.isArray(parsed.documents) && !parsed.error && !parsed.summary) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function matchesFilter(doc, filter) {
  if (!filter) return true;
  const q = filter.toLowerCase();
  const haystack = [
    doc.title,
    doc.summary,
    doc.source,
    doc.url,
    doc.publishedAt,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * Filterable Seerist document cards for automated sitrep sections. Renders
 * `fallback` when data_string is not a Seerist payload.
 */
export default function SeeristAutomatedSectionContent({
  dataString,
  fallback = null,
}) {
  const [filter, setFilter] = useState('');
  const payload = useMemo(() => parseSeeristPayload(dataString), [dataString]);

  if (!payload) return fallback;

  const documents = Array.isArray(payload.documents) ? payload.documents : [];
  const filtered = documents.filter((doc) => matchesFilter(doc, filter.trim()));

  return (
    <div className="SeeristAutomatedSectionContent">
      {payload.error && (
        <p className="SeeristAutomatedSectionContent__error">
          <strong>Seerist error:</strong> {payload.error}
        </p>
      )}

      {documents.length > 0 && (
        <div className="SeeristAutomatedSectionContent__filter mb-3">
          <StylishNewInput
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title, summary, or source…"
          />
          <small className="text-muted d-block mt-1">
            Showing {filtered.length} of {documents.length} documents
          </small>
        </div>
      )}

      {!payload.error && documents.length === 0 && (
        <p className="text-muted mb-0">
          No Seerist documents matched this request.
        </p>
      )}

      <div className="SeeristAutomatedSectionContent__list">
        {filtered.map((doc, index) => (
          <article
            key={doc.id || `${doc.url || 'doc'}-${index}`}
            className="SeeristAutomatedSectionContent__card"
          >
            <h4 className="SeeristAutomatedSectionContent__title">
              {doc.title || 'Untitled'}
            </h4>
            {(doc.publishedAt || doc.source) && (
              <div className="SeeristAutomatedSectionContent__meta">
                {doc.publishedAt && <time>{doc.publishedAt}</time>}
                {doc.publishedAt && doc.source ? ' · ' : null}
                {doc.source && <span>{doc.source}</span>}
              </div>
            )}
            {doc.summary && (
              <p className="SeeristAutomatedSectionContent__summary">
                {doc.summary}
              </p>
            )}
            {doc.url && (
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="SeeristAutomatedSectionContent__link"
              >
                Open source
              </a>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
